import React, { useEffect, useState } from 'react';
import { usePortletStore } from './App';
import ReactDOM from 'react-dom';
import { useViewStateStore } from './stores/viewState.store';
import { NotificationList } from './components/NotificationList/NotificationList';
import { useNotificationStore } from './stores/notification.store';
import {
  Breakpoints,
  Button,
  Checkbox,
  FocusTrap,
  intl,
  intlFmt,
  Toggle,
  Tool,
  useBreakpoint
} from 'ehs-react';
import type { icModel } from 'generated-ts-common';
import { PopupMenu } from './components/PopupMenu/PopupMenu';
import { getNotificationTitle } from './utils/utils';

type NotificationSearchParameters = icModel.saf.infrastructure.model.notification.NotificationSearchParameters;

export function MainView(): JSX.Element {
  const [userRole] = usePortletStore(store => [store.configuration.userRole]);

  return (
    <>
      {userRole && (
        <>
          <NotificationCenter />
          <NaviButton />
        </>
      )}
    </>
  );
}

function NotificationCenter(): JSX.Element {
  const [open, toggle] = useViewStateStore(store => [store.open, store.toggle]);
  const [userRole] = usePortletStore(store => [store.configuration.userRole]);
  const [
    markAllAsRead,
    filter,
    updateFilter,
    checkForNewNotifications,
    numberOfUnreadNotifications
  ] = useNotificationStore(store => [
    store.markAllAsRead,
    store.filter,
    store.updateFilter,
    store.checkForNewNotifications,
    store.numberOfUnreadNotifications
  ]);

  const showOnlyUnread = filter.readStatus === false;

  useEffect(() => {
    const toId = setInterval(checkForNewNotifications, 60000);
    return () => clearInterval(toId);
  }, [checkForNewNotifications]);

  useEffect(() => {
    if (open) {
      (document.querySelector(
        '[data-testid="close-notification-center-button"]'
      ) as HTMLElement)?.focus();
    }
  }, [open]);

  const mobileBp = useBreakpoint(Breakpoints.MOBILE);
  const mobileView = mobileBp && userRole === 'PATIENT';

  return ReactDOM.createPortal(
    <div
      className={`notification-center-container${
        mobileView ? ' nc-mobile-view' : ''
      }${open && mobileView ? ' mobile-open' : ''}${!open ? ' nc-hidden' : ''}`}
      aria-hidden={!open}
      data-testid="notification-center-container"
      tabIndex={open ? 0 : -1}
    >
      <FocusTrap active={mobileView && open}>
        <div
          className={`notification-center-${userRole} ${
            open ? 'notification-center-open' : ''
          }`}
        >
          <h1 className="notification-center-header">
            {mobileView && (
              <div className="left">
                <Button
                  data-testid="close-notification-center-button"
                  className="nc-icon-button"
                  variant="gray"
                  text={intl('view.button.close')}
                  icon="arrow-left"
                  onClick={toggle}
                />
              </div>
            )}
            {intl('notificationHook.view.title')}
            <div className="right">
              {userRole === 'PATIENT' && (
                <Button
                  data-testid="notification-settings-button"
                  className="nc-icon-button"
                  variant="gray"
                  icon="shui_settings_s"
                  text={intl('notificationCenter.notificationSettings')}
                  onClick={() =>
                    (window.location.href =
                      '/group/patient-portal/account-settings')
                  }
                />
              )}
              {!mobileView && (
                <Button
                  data-testid="close-notification-center-button"
                  className="nc-icon-button"
                  variant="gray"
                  icon="shui-cancel"
                  text={intl('view.button.close')}
                  onClick={toggle}
                />
              )}
            </div>
          </h1>
          {userRole === 'PHYSICIAN' && (
            <Toggle
              data-testid="show-only-unread-toggle"
              value={showOnlyUnread}
              onChange={val =>
                updateFilter({
                  ...filter,
                  readStatus: val ? false : undefined
                })
              }
              label={intl('notificationCenter.filter.showOnlyUnread')}
            />
          )}
          <div className={'nc-actions'}>
            {numberOfUnreadNotifications > 0 && (
              <Button
                data-testid="mark-all-as-read-button"
                className="mark-all-as-read-button"
                variant="gray"
                text={intl('notificationHook.view.markAll')}
                onClick={() => markAllAsRead()}
              />
            )}
            {numberOfUnreadNotifications <= 0 && <div />}
            {userRole === 'PHYSICIAN' && (
              <div className="right">
                <PopupMenu
                  icon="shui_filter"
                  className="filter-popup"
                  arialLabel={intl('view.button.filter')}
                  dataTestId="notification-filter-button"
                >
                  <NotificationFilter />
                </PopupMenu>
                <Button
                  data-testid="notification-settings-button"
                  className="nc-icon-button"
                  variant="gray"
                  icon="shui_settings_s"
                  text={intl('notificationCenter.notificationSettings')}
                  onClick={() =>
                    (window.location.href =
                      '/group/physician-portal/account-settings')
                  }
                />
              </div>
            )}
          </div>
          <NotificationList />
        </div>
      </FocusTrap>
    </div>,
    document.body
  );
}

function NaviButton(): JSX.Element {
  const [open, toggle] = useViewStateStore(store => [store.open, store.toggle]);
  const [
    numberOfUnreadNotifications,
    getNumberOfUnreadNotifications
  ] = useNotificationStore(store => [
    store.numberOfUnreadNotifications,
    store.getNumberOfUnreadNotifications
  ]);

  const isMobile = useBreakpoint(Breakpoints.MOBILE);

  const [isMobileDeferred, setIsMobileDeferred] = useState(isMobile);

  useEffect(() => {
    // avoid issues if somebody resizes the screen very fast -> in this case 'notification-center-hook-btn' could not be ready, yet
    setTimeout(() => setIsMobileDeferred(isMobile), 50);
  }, [isMobile]);

  useEffect(() => {
    getNumberOfUnreadNotifications();
  }, [getNumberOfUnreadNotifications]);

  let rightMenuContainer = document.getElementById(
    'additionalNaviContentRight'
  );

  const dashboardHeader = document.getElementById(
    'notification-center-hook-btn'
  );

  if (isMobileDeferred && dashboardHeader) {
    rightMenuContainer = dashboardHeader;
  }

  if (rightMenuContainer) {
    return ReactDOM.createPortal(
      <div
        className={
          'notification-center-navi-button' +
          (open ? ' notification-center-navi-button-open' : '')
        }
      >
        <Button
          data-testid="toggle-notification-center-button"
          className="notification-center-button"
          variant="gray"
          text={
            intl('notificationCenter.action.open') +
            ' - ' +
            intlFmt(
              'notificationCenter.numberOfUnreadNotifications',
              numberOfUnreadNotifications + ''
            )
          }
          icon="bell"
          onClick={toggle}
        />
        {numberOfUnreadNotifications > 0 && (
          <div className="nc-unread-marker" data-testid="nc-unread-marker" />
        )}
      </div>,
      rightMenuContainer
    );
  } else {
    return <></>;
  }
}

function NotificationFilter(): JSX.Element {
  const [availableTopicTypes] = usePortletStore(store => [
    store.configuration.availableTopicTypes
  ]);
  const [filter, updateFilter] = useNotificationStore(store => [
    store.filter,
    store.updateFilter
  ]);

  return (
    <div className="notification-filter">
      {availableTopicTypes.map(topic => (
        <Checkbox
          key={'notification-filter-' + topic}
          label={getNotificationTitle(topic)}
          value={filter?.topicType?.split(',')?.includes(topic)}
          onChange={state => {
            let selTopics = [];
            if (state) {
              selTopics = [...(filter?.topicType?.split(',') ?? []), topic];
            } else {
              selTopics = [
                ...(filter?.topicType?.split(',') ?? []).filter(
                  t => t !== topic
                )
              ];
            }
            const updatedFilter: Partial<NotificationSearchParameters> = {
              topicType:
                selTopics.length <= 0 ? '---none---' : selTopics.join(',')
            };
            updateFilter(updatedFilter);
          }}
        />
      ))}
    </div>
  );
}
